import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Responsive, WidthProvider, type Layout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import OfficeBookingDrawer from '../components/OfficeBookingDrawer';
import { useAddPayment } from '../contexts/AddPaymentContext';
import { loadBuyerNames, useUnitPageData } from '../office/unit/useUnitPageData';
import { buildUnitSummary } from '../office/unit/unitSummary';
import { stageOfStep, type StageKey } from '../office/unit/lifecycle';
import { bookingDocumentRows, unitAreas } from '../office/unit/unitExtras';
import { useUnitLayout } from '../office/unit/useUnitLayout';
import { changeChangeOrderStatus } from '../office/changeOrders';
import { getFirestore } from 'firebase/firestore';
import { UnitCustomize } from '../office/unit/UnitCustomize';
import { GRID_GAP, GRID_ROW, UNIT_CARDS, UNIT_CARD_BY_ID, cardLabel, placeCards, rowsForPx, type GridItem } from '../office/unit/unitLayout';
import { LAYOUT_OWNER_EMAIL, useTodayPrefs } from '../office/today/useTodayPrefs';
import { accentVars } from '../office/today/palettes';
import { BuyerFormCard } from '../office/unit/BuyerFormCard';
import { AreasCard, ChangeOrdersCard, CollectionCard, DatesCard, MilestonesCard, DocumentsCard, WhatsAppCard } from '../office/unit/UnitMoreCards';
import {
  UIcon,
  AttentionCard,
  BuyersCard,
  ChargesCard,
  LifecycleCard,
  LoanCard,
  PendingWorkCard,
  PaymentCard,
  ReceiptsCard,
  StepBar,
  UnitHeader,
  short,
} from '../office/unit/UnitCards';
import type { DrawerActionType, UnitStep } from '../office/today/workflowBoard';
import '../office/today/today.css';
import '../office/unit/unit.css';

// focus: open the booking drawer on one section ('documents', 'payments', 'loan', 'customers', 'change-orders').
type DrawerState = { action: { type: DrawerActionType; nonce: number; unitId: string; txnId?: string; docKey?: string } | null; focus?: string } | null;

const Grid = WidthProvider(Responsive);
// Below this the cards stack (phones); at any wider size the full 12-column grid can be arranged and saved.
const STACK_BELOW = 600;
const PHONE = '(max-width: 700px)';
const usePhone = () => {
  const [phone, setPhone] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.(PHONE).matches);
  useEffect(() => {
    const mq = window.matchMedia?.(PHONE);
    if (!mq) return;
    const on = () => setPhone(mq.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  return phone;
};

// Only three statuses are shown: Available, Hold (form filled or blocked, no money yet) and Booked
// (first customer receipt in). `paid` is known on the unit's own page; lists fall back to the saved status.
const statusLabel = (status: string, hasForm: boolean, paid?: boolean) => {
  const s = String(status || '').toLowerCase();
  if (paid === true) return 'Booked';
  if (paid === false) return hasForm || s === 'blocked' || s === 'booked' || s === 'sold' ? 'Hold' : 'Available';
  if (s === 'sold' || s === 'booked') return 'Booked';
  return s === 'blocked' || hasForm ? 'Hold' : 'Available';
};

export default function OfficeUnitDetailPage() {
  const { unitId } = useParams<{ unitId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const fromToday = (location.state as any)?.fromOfficeToday === true;
  const { currentProjectId, currentUser, roleId } = useAuth();
  const { saved: savedGrid, hidden: hiddenList, isOwn, save: saveLayout, saveAsDefault } = useUnitLayout(currentUser?.uid);
  const hiddenSet = useMemo(() => new Set(hiddenList), [hiddenList]);
  const placed = useMemo(() => placeCards(savedGrid, hiddenSet), [hiddenSet, savedGrid]);
  const ordered = useMemo(() => placed.slice().sort((a, b) => a.y - b.y || a.x - b.x), [placed]);
  const rootRef = useRef<HTMLDivElement>(null);
  const gridHostRef = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState<Record<string, number>>({});
  const [width, setWidth] = useState(1200);
  const stacked = width < STACK_BELOW;
  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const { prefs: colours, update: updateColours } = useTodayPrefs(currentUser?.uid);
  const [customizing, setCustomizing] = useState(false);
  // Same as the Change Orders page: only admins change a change order's status.
  const canEditCo = ['super-admin', 'admin'].includes(String(roleId || '')) && !!currentProjectId && !!currentUser?.email;
  const { openAddPayment } = useAddPayment();
  const data = useUnitPageData(currentProjectId, unitId);
  const { unit, units } = data;
  const [drawer, setDrawer] = useState<DrawerState>(null);
  const [pickedStep, setPickedStep] = useState(-1);
  const [pickedStage, setPickedStage] = useState<StageKey | null>(null);
  const [chatSignal, setChatSignal] = useState(0);
  const [switchSignal, setSwitchSignal] = useState(0);
  const [buyerNames, setBuyerNames] = useState<Map<string, string> | null>(null);
  const loadNames = () => {
    if (buyerNames || !currentProjectId || !units.length) return;
    loadBuyerNames(currentProjectId, units).then(setBuyerNames).catch((err) => console.warn('Unit page: load buyer names failed', err));
  };
  useEffect(() => setBuyerNames(null), [currentProjectId]);
  const phone = usePhone();

  const s = useMemo(
    () =>
      unit && !data.loading
        ? buildUnitSummary({
            unit,
            booking: data.booking,
            bookingHistoryCount: data.bookingHistoryCount,
            customers: data.customers,
            txns: data.txns,
            projectMeta: data.projectMeta,
            mainCase: data.mainCase,
            loanCase: data.loanCase,
            mainSteps: data.mainSteps,
            loanSteps: data.loanSteps,
            changeOrders: data.changeOrders,
          })
        : null,
    [data.booking, data.bookingHistoryCount, data.changeOrders, data.customers, data.loanCase, data.loanSteps, data.loading, data.mainCase, data.mainSteps, data.projectMeta, data.txns, unit]
  );

  const docs = useMemo(() => bookingDocumentRows(data.uploadedDocs, data.draftDocs, data.docConfig), [data.uploadedDocs, data.draftDocs, data.docConfig]);
  const areas = useMemo(() => (unit ? unitAreas(unit, data.projectMeta) : null), [data.projectMeta, unit]);

  // Measure each card's content so its grid slot is exactly as tall as the card (as on the Today page).
  useEffect(() => {
    const host = gridHostRef.current;
    if (!host) return;
    // A short timer rather than requestAnimationFrame: frames pause in background tabs and panes.
    let timer: number | undefined;
    const measure = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        const next: Record<string, number> = {};
        host.querySelectorAll<HTMLElement>('.ot-gi').forEach((gi) => {
          const card = gi.querySelector<HTMLElement>(':scope > .ot-card');
          const last = card?.lastElementChild as HTMLElement | null;
          if (!card || !last || !gi.dataset.id) return;
          const cs = getComputedStyle(card);
          next[gi.dataset.id] = rowsForPx(last.offsetTop + last.offsetHeight + parseFloat(cs.paddingBottom) + parseFloat(cs.borderBottomWidth));
        });
        setFit((prev) => (Object.keys(next).length === Object.keys(prev).length && Object.keys(next).every((k) => prev[k] === next[k]) ? prev : next));
      }, 30);
    };
    const mo = new MutationObserver(measure);
    mo.observe(host, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['aria-expanded'] });
    const ro = new ResizeObserver(measure);
    ro.observe(host);
    measure();
    return () => {
      mo.disconnect();
      ro.disconnect();
      window.clearTimeout(timer);
    };
  }, [data.loading, stacked, s, placed, unitId]);

  const idx = unit ? units.findIndex((u) => u.id === unit.id) : -1;
  const go = (i: number) => navigate(`/office/units/${units[i].id}`, { state: location.state });
  const bookingId = String(unit?.activeBookingId || '').trim();
  const isBooked = ['booked', 'sold'].includes(String(unit?.bookingStatus || '').toLowerCase()) && !!bookingId;
  const primary = s?.buyers.find((b) => b.primary) || s?.buyers[0];

  const recordReceipt = () =>
    unit &&
    openAddPayment({
      unitId: unit.id,
      unitNumber: unit.unitNumber || undefined,
      bookingId: bookingId || undefined,
      partyName: primary?.name || undefined,
      receiptCategory: 'customer',
    });
  const openFull = () => setDrawer({ action: null });
  const openSection = (focus: string) => setDrawer({ action: null, focus });
  const fillHere = () => currentProjectId && unit && bookingId && navigate(`/buyer-form/office/${currentProjectId}/${unit.id}/${bookingId}`);
  const runStep = (step: UnitStep) =>
    setDrawer({ action: step.action && unit ? { type: step.action.type, nonce: Date.now(), unitId: unit.id } : null });
  const pickStep = (i: number) => {
    setPickedStep(i);
    const steps = s?.steps || [];
    let prev: StageKey | null = null;
    let stage: StageKey = 'booking';
    for (let k = 0; k <= i && k < steps.length; k += 1) {
      stage = stageOfStep(steps[k], prev);
      if (steps[k].workflowType === 'main') prev = stage;
    }
    setPickedStage(stage);
    document.getElementById(`stage-${stage}`)?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
  };

  // Tab title names the unit, so several open units can be told apart.
  useEffect(() => {
    if (!unit) return;
    const before = document.title;
    document.title = `${unit.unitNumber || unit.id} · Units`;
    return () => {
      document.title = before;
    };
  }, [unit]);

  // Shortcuts: K next unit, J previous, R record receipt, / switch unit. Ignored while typing or with a drawer / dialog open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((drawer && !drawer.action) || e.metaKey || e.ctrlKey || e.altKey) return;
      const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      if (!['j', 'k', 'r', '/'].includes(key)) return;
      const el = e.target as HTMLElement | null;
      if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) return;
      if (document.querySelector('.MuiModal-root:not(.MuiModal-hidden), .ou-switchpop')) return;
      if (key === '/') {
        e.preventDefault();
        setSwitchSignal(Date.now());
        return;
      }
      if (key === 'r') {
        if (!isBooked) return;
        e.preventDefault();
        recordReceipt();
        return;
      }
      const to = key === 'j' ? idx - 1 : idx + 1;
      if (idx < 0 || to < 0 || to >= units.length) return;
      e.preventDefault();
      navigate(`/office/units/${units[to].id}`, { state: location.state });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const switcherUnits = useMemo(
    () =>
      units.map((u) => ({
        id: u.id,
        unitNumber: String(u.unitNumber || u.id),
        status: statusLabel(String(u.bookingStatus || ''), !!String(u.activeBookingId || '').trim()),
        buyer: buyerNames?.get(u.id) || String(u.primaryCustomerName || u.customerName || u.allotteeName || '').trim(),
      })),
    [buyerNames, units]
  );

  const header = unit ? (
    <UnitHeader
      unitNumber={String(unit.unitNumber || unit.id)}
      status={statusLabel(String(unit.bookingStatus || ''), !!bookingId, s ? !!s.dates.booking : undefined)}
      s={isBooked ? s : null}
      sub={
        <>
          {primary?.name ? <><span>{primary.name}</span><span className="faint">·</span></> : null}
          <span>{[unit.usage, unit.blockType].filter(Boolean).join(', ') || 'Unit'}</span>
          {s?.dates.booking || s?.dates.form ? <><span className="faint">·</span><span>{s.dates.booking ? 'Booked' : 'On hold since'} {(s.dates.booking || s.dates.form)!.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span></> : null}
        </>
      }
      backLabel={fromToday ? 'Today' : 'Units'}
      onBack={() => navigate(fromToday ? '/office/today' : '/office/units')}
      prev={idx > 0 ? () => go(idx - 1) : null}
      next={idx >= 0 && idx < units.length - 1 ? () => go(idx + 1) : null}
      onRecord={isBooked ? recordReceipt : null}
      onFullView={bookingId ? openFull : null}
      onClassic={() => navigate(`/office/units/${unit.id}/classic`, { state: location.state })}
      onBookingForm={() => setDrawer({ action: { type: 'booking_form', nonce: Date.now(), unitId: unit.id } })}
      bookingFormFile={data.bookingForm.file}
      bookingFormDetails={data.bookingForm.details}
      onCustomize={isBooked ? () => setCustomizing((v) => !v) : null}
      switcher={{ units: switcherUnits, openSignal: switchSignal, onOpen: loadNames, onPick: (id) => id !== unit.id && navigate(`/office/units/${id}`, { state: location.state }) }}
    />
  ) : null;

  let body: React.ReactNode;
  if (!currentProjectId) {
    body = (
      <div className="ot-card ot-blank">
        <h3 style={{ margin: '0 0 6px' }}>Choose a project</h3>
        <p className="muted" style={{ margin: 0 }}>Pick a project from the menu to see its units.</p>
      </div>
    );
  } else if (data.loading) {
    // Same shape as the loaded page so nothing jumps when the cards arrive.
    body = (
      <div aria-busy="true" aria-label="Loading unit">
        <div className="ot-skel" style={{ height: 74, maxWidth: 420, margin: '0 4px 18px', borderRadius: 16 }} />
        <div className="ot-grid">
          <div className="ot-skel ot-s7" style={{ height: 290 }} />
          <div className="ot-skel ot-s5" style={{ height: 290 }} />
          <div className="ot-skel ot-s12" style={{ height: 190 }} />
          <div className="ou-col ot-s7"><div className="ot-skel" style={{ height: 520 }} /></div>
          <div className="ou-col ot-s5"><div className="ot-skel" style={{ height: 240 }} /><div className="ot-skel" style={{ height: 220 }} /></div>
        </div>
      </div>
    );
  } else if (!unit) {
    body = (
      <div className="ot-card ot-blank">
        <h3 style={{ margin: '0 0 6px' }}>Unit not found</h3>
        <p className="muted" style={{ margin: '0 0 14px' }}>It may have been removed or belongs to another project.</p>
        <button className="ot-btn" onClick={() => navigate('/office/units')}>Back to units</button>
      </div>
    );
  } else if (!isBooked || !s) {
    // Short version for units that are not booked yet.
    body = (
      <>
      <div className="ot-card ou-short">
        <div className="ot-hd"><div><h3>{bookingId ? 'Booking form filled, not booked yet' : 'This unit is not booked'}</h3><p>{bookingId ? 'Confirm the booking to start tracking payments and paperwork.' : 'Book it to start tracking payments and paperwork.'}</p></div></div>
        <div className="ot-kv"><span className="muted">Price</span><b className="num">{unit.unitAmount ? short(Number(unit.unitAmount)) : 'Not set'}</b></div>
        <div className="ot-kv"><span className="muted">Type</span><span>{[unit.usage, unit.blockType].filter(Boolean).join(', ') || '-'}</span></div>
        {areas && areas.rows.length ? (
          <>
            {areas.rows.map((r) => <div className="ot-kv" key={r.label}><span className="muted">{r.label}</span><span className="num">{r.sqmt} sq mt</span></div>)}
            <div className="ot-kv"><span className="muted">Total</span><b className="num">{areas.totalSqft.toLocaleString('en-IN')} sq ft{areas.sbSqft ? ` · super built-up ${areas.sbSqft.toLocaleString('en-IN')} sq ft` : ''}</b></div>
          </>
        ) : null}
        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button className="ot-btn dark" onClick={openFull}>{bookingId ? 'Open booking form' : 'Book unit'}</button>
          <button className="ot-btn" onClick={() => navigate(`/office/units/${unit.id}/classic`, { state: location.state })}>Classic view</button>
        </div>
      </div>
      {bookingId && currentProjectId && canEditCo ? (
        <div style={{ marginTop: 14, maxWidth: 560 }}>
          <BuyerFormCard projectId={currentProjectId} unitId={unit.id} bookingId={bookingId} defaultName="" defaultMobile="" onFillHere={fillHere} />
        </div>
      ) : null}
      </>
    );
  } else {
    // Every card the page can show; the saved layout decides where each goes and which are hidden.
    // A card with nothing to show is left out (loading and load errors still show, so nothing looks falsely empty).
    const legal = s.legal;
    const hasCharges = s.gst.due > 0 || Object.values(legal).some((o) => o.due > 0 || o.paid > 0);
    const hasMilestones = !!(data.booking?.paymentCondition?.stages?.length || String(data.booking?.paymentTerms || '').trim());
    const docsState = data.failed.documents ? 'failed' : data.docsLoaded ? 'ready' : 'loading';
    const coState = data.failed.changeOrders ? 'failed' : data.changeOrdersLoaded ? 'ready' : 'loading';
    const cards: Record<string, React.ReactNode> = {
      payment: <PaymentCard s={s} />,
      attention: s.attention.length ? <AttentionCard s={s} /> : null,
      pending: s.steps && s.steps.some((x) => x.status === 'ready') ? <PendingWorkCard steps={s.steps} onAction={runStep} onOpen={(which) => openSection(which === 'loan' ? 'loan' : 'pending-work')} /> : null,
      steps: s.steps && s.steps.length ? <StepBar steps={s.steps} picked={pickedStep >= 0 ? pickedStep : [s.steps.findIndex((x) => x.status === 'ready'), s.steps.findIndex((x) => x.status !== 'done'), 0].find((i) => i >= 0) as number} onPick={pickStep} onAction={runStep} /> : null,
      lifecycle: s.lifecycle.length ? (
        <LifecycleCard s={s} picked={pickedStage} onAction={runStep} onRecord={recordReceipt} />
      ) : !data.failed.project ? null : (
        <div className="ot-card">
          <div className="ot-hd"><div><h3>Lifecycle</h3><p>{data.failed.project ? "Couldn't load the project's workflow. Refresh the page to try again." : 'No office workflow is tracking this booking yet. Opening Full booking view sets it up.'}</p></div></div>
          {!data.failed.project ? <button className="ot-btn sm" onClick={openFull}>Open Full booking view</button> : null}
        </div>
      ),
      documents: docsState !== 'ready' || docs.length ? <DocumentsCard state={docsState} docs={docs} onManage={() => openSection('documents')} onRegenerate={(docKey) => setDrawer({ action: { type: 'regenerate_doc', nonce: Date.now(), unitId: unit.id, docKey } })} onEditSanction={canEditCo ? (docKey) => setDrawer({ action: { type: 'edit_sanction', nonce: Date.now(), unitId: unit.id, docKey } }) : undefined} lockedTemplates={data.bookingForm.signed ? ['booking_form_v1'] : []} /> : null,
      changeOrders: coState !== 'ready' || s.changeOrders.length ? <ChangeOrdersCard state={coState} rows={s.changeOrders} onStatus={canEditCo ? (id, from, to) => changeChangeOrderStatus(getFirestore(), currentProjectId!, id, from, to, String(currentUser?.email || '')) : undefined} onOpen={() => (s.changeOrders.length ? openSection('change-orders') : navigate('/office/change-orders'))} /> : null,
      whatsapp: currentProjectId && s.buyers.some((b) => b.mobile) ? <WhatsAppCard openSignal={chatSignal} s={s} unitId={unit.id} unitNumber={String(unit.unitNumber || unit.id)} projectId={currentProjectId} /> : null,
      collection: <CollectionCard s={s} />,
      milestones: !hasMilestones ? null : (
        <MilestonesCard
          condition={data.booking?.paymentCondition}
          bookingDate={String(data.booking?.bookingFormDate || '')}
          legacyTerms={data.booking?.paymentTerms}
          unitValue={s.value}
          onEdit={() => setDrawer({ action: { type: 'booking_form', nonce: Date.now(), unitId: unit.id } })}
        />
      ),
      receipts: !s.receipts.length ? null : <ReceiptsCard compact={phone} s={s} onRecord={recordReceipt} onReceipts={() => openSection('payments')} onDownload={(txnId) => setDrawer({ action: { type: 'download_receipt', nonce: Date.now(), unitId: unit.id, txnId } })} />,
      dates: <DatesCard s={s} />,
      charges: !hasCharges ? null : <ChargesCard s={s} onManage={() => openSection('payments')} />,
      loan: (
        <LoanCard
          s={s}
          onManage={() => openSection('loan')}
          onStart={() => setDrawer({ action: { type: 'start_loan', nonce: Date.now(), unitId: unit.id } })}
          onStop={() => setDrawer({ action: { type: 'stop_loan', nonce: Date.now(), unitId: unit.id } })}
          onEditSanction={canEditCo ? () => setDrawer({ action: { type: 'edit_sanction', nonce: Date.now(), unitId: unit.id } }) : undefined}
        />
      ),
      buyers: !s.buyers.length && !s.broker ? null : <BuyersCard s={s} onEdit={() => openSection('customers')} />,
      buyerForm: currentProjectId && canEditCo ? (
        <BuyerFormCard
          projectId={currentProjectId}
          unitId={unit.id}
          bookingId={bookingId}
          defaultName={s.buyers.find((x) => x.primary)?.name || s.buyers[0]?.name || ''}
          defaultMobile={s.buyers.find((x) => x.primary)?.mobile || s.buyers[0]?.mobile || ''}
          onFillHere={fillHere}
        />
      ) : null,
      areas: areas && areas.rows.length ? <AreasCard areas={areas} /> : null,
    };
    // Same grid as the Today page: width and position are the user's, height always fits the content.
    const visible = ordered.filter((l) => cards[l.i]);
    if (stacked) {
      body = <div className="ot-stack">{visible.map((l) => <React.Fragment key={l.i}>{cards[l.i]}</React.Fragment>)}</div>;
    } else {
      const sized = (l: GridItem) => {
        const h = fit[l.i] || rowsForPx(UNIT_CARD_BY_ID.get(l.i)?.px || 300);
        return { ...l, h, minW: UNIT_CARD_BY_ID.get(l.i)?.minW || 3, minH: h, maxH: h };
      };
      const persist = (next: Layout[]) => {
        // Cards not shown for this unit (e.g. no loan) keep their saved place.
        const shown = new Set(next.map((l) => l.i));
        const keep = placed.filter((l) => !shown.has(l.i));
        saveLayout({ layout: [...next.map(({ i, x, y, w, h }) => ({ i, x, y, w, h })), ...keep], hidden: hiddenList });
      };
      body = (
        <div ref={gridHostRef}>
          <Grid
            className="ot-rgl"
            layouts={{ lg: visible.map(sized) }}
            breakpoints={{ lg: 0 }}
            cols={{ lg: 12 }}
            rowHeight={GRID_ROW}
            margin={[GRID_GAP, 0]}
            containerPadding={[0, 0]}
            isDraggable={customizing}
            isResizable={customizing}
            draggableHandle=".ot-grip"
            compactType="vertical"
            onDragStop={persist}
            onResizeStop={persist}
          >
            {visible.map((l) => (
              <div key={l.i} className="ot-gi" data-id={l.i}>
                <div className="ot-editbar">
                  <span className="ot-grip" aria-label={`Drag to move ${cardLabel(l.i)}`} title="Drag to move">
                    <svg className="ot-ic" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      {[6, 12, 18].map((y) => <React.Fragment key={y}><circle cx="9" cy={y} r="1.5" /><circle cx="15" cy={y} r="1.5" /></React.Fragment>)}
                    </svg>
                  </span>
                  <span className="name">{cardLabel(l.i)}</span>
                  <button className="ot-hide" onClick={() => saveLayout({ layout: placed.filter((p) => p.i !== l.i), hidden: [...hiddenList, l.i] })} aria-label={`Hide ${cardLabel(l.i)}`} title="Hide card">
                    <svg className="ot-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" /></svg>
                  </button>
                </div>
                {cards[l.i]}
              </div>
            ))}
          </Grid>
        </div>
      );
    }
  }

  return (
    <div className="ou-scroll">
      <div className={`ot${customizing && !stacked ? ' ot-editing' : ''}`} data-palette={colours.palette} style={accentVars(colours.accent)} ref={rootRef}>
        {header}
        {customizing && isBooked ? (
          <UnitCustomize
            hiddenIds={UNIT_CARDS.filter((c) => hiddenSet.has(c.id)).map((c) => c.id)}
            onAdd={(id) => {
              const bottom = placed.reduce((m, l) => Math.max(m, l.y + l.h), 0);
              const def = UNIT_CARD_BY_ID.get(id);
              if (def) saveLayout({ layout: [...placed, { i: id, x: 0, y: bottom, w: def.w, h: rowsForPx(def.px) }], hidden: hiddenList.filter((h) => h !== id) });
            }}
            onReset={() => saveLayout(null)}
            canReset={isOwn}
            canSetDefault={String(currentUser?.email || '').toLowerCase() === LAYOUT_OWNER_EMAIL}
            onSaveDefault={() => saveAsDefault({ layout: placed, hidden: hiddenList }, String(currentUser?.email || ''))}
            stacked={stacked}
            palette={colours.palette}
            accent={colours.accent}
            onColour={(patch) => updateColours(patch)}
          />
        ) : null}
        {data.error ? <div className="ot-alert" role="alert">{data.error}</div> : null}
        {!data.error && data.failed.receipts ? <div className="ot-alert" role="alert">Couldn't load this unit's receipts, so the money figures below may be wrong. Refresh the page to try again.</div> : null}
        {body}
      {unit && isBooked && s ? (
        <nav className="ou-bar" aria-label="Unit actions">
          {primary?.mobile ? <a className="ot-btn" href={`tel:${primary.mobile.replace(/\s+/g, '')}`}><UIcon name="phone" /> Call</a> : null}
          {primary?.mobile ? <button className="ot-btn" onClick={() => setChatSignal(Date.now())}>WhatsApp</button> : null}
          <button className="ot-btn dark" onClick={recordReceipt}><UIcon name="plus" /> Record receipt</button>
        </nav>
      ) : null}
      </div>
      {unit && currentProjectId ? (
        <OfficeBookingDrawer
          open={!!drawer}
          onClose={() => {
            setDrawer(null);
            data.reload();
          }}
          projectId={currentProjectId}
          unit={unit as any}
          actionRequest={drawer?.action || null}
          actionOnly={!!drawer?.action}
          focusSection={drawer?.focus}
        />
      ) : null}
    </div>
  );
}
